import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import FloatingBubbles from './FloatingBubbles';

const CompanyPresentationSection = () => {
  const { language } = useLanguage();

  return (
    <section className="py-12 bg-white relative overflow-hidden">
      <FloatingBubbles count={10} />
      <div className="max-w-[1800px] mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Image à gauche */}
          <div className="order-2 lg:order-1">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/166b91da-a769-4162-9f6c-f6ec47415337/images/7c574a94a58c3cbf1563cec5a883126483017102a1749b282975764caa611e1d.jpeg"
                alt="Global Construction & Ingénierie"
                className="w-full h-auto"
              />
            </div>
          </div>

          {/* Texte à droite */}
          <div className="order-1 lg:order-2 space-y-6">
            <div className="mb-6">
              <div className="inline-block">
                <p className="text-base font-semibold text-orange-500 uppercase tracking-wider mb-2">
                  {language === 'fr' ? 'Initiatives & Énergies Locales' : 'Local Initiatives & Energies'}
                </p>
                <h2 className="text-6xl font-bold mb-4" style={{ color: '#702963' }}>
                  {language === 'fr' ? 'Entreprise' : 'Company'}
                </h2>
                <div className="h-1 w-20 bg-orange-500 mb-4"></div>
                <h3 className="text-3xl font-bold text-gray-800">
                  {language === 'fr' ? 'd\'énergies renouvelables' : 'of renewable energies'}
                </h3>
              </div>
            </div>

            <div className="prose prose-lg max-w-none">
              <h4 className="text-2xl font-bold text-gray-800 mb-4">
                {language === 'fr' ? 'PRÉSENTATION DE L\'ENTREPRISE' : 'COMPANY PRESENTATION'}
              </h4>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                {language === 'fr'
                  ? 'Fondée en 2016, notre entreprise s\'est imposée en moins de dix ans comme un acteur reconnu dans le secteur de la construction et de la réhabilitation de bâtiments. Depuis sa création, elle a su développer une expertise solide sur l\'ensemble des corps d\'état du bâtiment, en intervenant aussi bien sur des projets neufs que sur des opérations de rénovation et de réhabilitation.'
                  : 'Founded in 2016, our company has established itself in less than ten years as a recognized player in the building construction and rehabilitation sector. Since its creation, it has developed solid expertise across all building trades, working on both new projects and renovation and rehabilitation operations.'}
              </p>

              <h5 className="text-2xl font-bold text-gray-800 mb-3 mt-6">
                {language === 'fr' ? 'Expérience et références' : 'Experience and References'}
              </h5>
              <p className="text-gray-700 leading-relaxed mb-4">
                {language === 'fr'
                  ? 'Forte de près de 9 années d\'activité, l\'entreprise justifie d\'un portefeuille de références diversifié : logements collectifs, bâtiments tertiaires, équipements publics et locaux industriels. Chaque chantier est conduit dans le respect des délais contractuels, des normes techniques en vigueur (DTU, Eurocodes) et des exigences de qualité fixées par la maîtrise d\'ouvrage.'
                  : 'With nearly 9 years of activity, the company has a diversified portfolio of references: collective housing, tertiary buildings, public facilities and industrial premises. Each project is conducted in compliance with contractual deadlines, current technical standards (DTU, Eurocodes) and quality requirements set by the project owner.'}
              </p>

              <h5 className="text-2xl font-bold text-gray-800 mb-3 mt-6">
                {language === 'fr' ? 'Moyens humains' : 'Human Resources'}
              </h5>
              <p className="text-gray-700 leading-relaxed mb-4">
                {language === 'fr'
                  ? 'L\'entreprise dispose d\'une équipe qualifiée et expérimentée, composée de compagnons issus des métiers du gros œuvre, du second œuvre et des travaux spécialisés. Ses encadrants de chantier justifient de formations continues et d\'habilitations à jour, garantissant la maîtrise des règles de l\'art et des obligations réglementaires en matière de sécurité (PPSPS, plan de prévention).'
                  : 'The company has a qualified and experienced team, consisting of workers from structural work, finishing work and specialized trades. Its site supervisors have continuous training and up-to-date certifications, ensuring mastery of best practices and regulatory safety obligations.'}
              </p>

              <h5 className="text-2xl font-bold text-gray-800 mb-3 mt-6">
                {language === 'fr' ? 'Démarche qualité et environnementale' : 'Quality and Environmental Approach'}
              </h5>
              <p className="text-gray-700 leading-relaxed mb-4">
                {language === 'fr'
                  ? 'Depuis sa création, l\'entreprise s\'inscrit dans une démarche de progrès continu. Elle veille au respect de la réglementation environnementale sur ses chantiers : gestion rigoureuse des déchets (tri sélectif, valorisation > 90 %), réduction des nuisances sonores et visuelles, et information systématique des riverains. Elle s\'engage à utiliser des matériaux conformes aux exigences des marchés publics et privés.'
                  : 'Since its creation, the company has been committed to continuous improvement. It ensures compliance with environmental regulations on its sites: rigorous waste management (selective sorting, recovery > 90%), reduction of noise and visual nuisances, and systematic information to residents. It commits to using materials that meet public and private market requirements.'}
              </p>

              <h5 className="text-2xl font-bold text-gray-800 mb-3 mt-6">
                {language === 'fr' ? 'Capacités financières et juridiques' : 'Financial and Legal Capabilities'}
              </h5>
              <p className="text-gray-700 leading-relaxed mb-4">
                {language === 'fr'
                  ? 'L\'entreprise présente une situation financière saine, avec une progression régulière de son chiffre d\'affaires depuis sa création. Elle est à jour de l\'ensemble de ses obligations fiscales et sociales, et dispose des assurances réglementaires obligatoires : responsabilité civile professionnelle, décennale et dommages-ouvrage.'
                  : 'The company has a healthy financial situation, with steady growth in turnover since its creation. It is up to date with all tax and social obligations, and has mandatory regulatory insurance: professional civil liability, ten-year and project owner insurance.'}
              </p>

              <h5 className="text-2xl font-bold text-gray-800 mb-3 mt-6">
                {language === 'fr' ? 'Engagement' : 'Commitment'}
              </h5>
              <p className="text-gray-700 leading-relaxed">
                {language === 'fr'
                  ? 'Au-delà de la technicité, l\'entreprise place la relation de confiance avec ses clients, maîtres d\'ouvrage et maîtres d\'œuvre au cœur de son fonctionnement. Sa réactivité, sa transparence et son sens du service en font un partenaire fiable pour des projets de toute envergure.'
                  : 'Beyond technical expertise, the company places trust relationships with its clients, project owners and project managers at the heart of its operations. Its responsiveness, transparency and sense of service make it a reliable partner for projects of all sizes.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyPresentationSection;
